// Utils
import {
  updateUrlSearch,
  getScreenOrientation,
  mod,
  uploadFile,
  ComponentManager,
  DomApi,
  asyncSleep
} from "./utils";

// Components
import { DragController } from "./drag_controller";
import { ImageEditorComponent } from "./components/image_editor";
import { UploadComponent } from "./components/upload";
import { UploadPricingBehaviour } from "./components/upload_pricing_behavior";
import { SwatchColorParentSelectBehaviour } from "./components/swatch_color_parent_select_behavior";
import { GroupBtnAutoSelectComponent } from "./components/group_btn_auto_select";
import { LoadStateGroupNav } from "./components/load_state_group_nav";
import { NoSelectionComponent } from "./components/no_selection";
import { MatchSelectionsBehavior } from "./components/match_selections";

// Section controllers
import { CableOptions } from "./controllers/cable_options";
import { LectrosonicsOptions } from "./controllers/lectrosonics_options";
import { SwatchGallery } from "./controllers/swatch_gallery";
import { BoxOptions } from "./controllers/box_options";

frappe.provide('jhaudio.designer');

/**
 * Custom code organization:
 * - Controllers manage overall ui behaviour. They group specific components implementations
 * - Components manage individual extensions to existing controllers. They usually only add a single feature.
 */

jhaudio.designer.Controller = class {
  constructor($container, config, iems, iem_code, iem_owners, rush_items, settings, edit_state, edit_id) {
    this.$container = $container;
    this.$footer = $('.jha-designer-footer');
    this.$formContainer = this.$container.find('.form-container');
    this.imageEditorComponent = undefined;
    this.uploadPricingComponent = undefined;
    this.lectrosonicsOptionsController = undefined;
    this.$lightToggle = $('#light-toggle');
    this.$wait = $('<div class="designer-wait"><div class="loader">Loading...</div></div>');
    this.config = config;
    this.iem_owners = iem_owners;
    this.controllers = {};
    this.swatches = {};
    this.iems = iems;
    this.iemsByCode = {};
    this.groupsByName = {};
    this.ownerByName = {};
    this.cable = null;
    this.lectrosonics = null;
    this.blacklist = [];
    this.settings = settings;
    this.rush_items = rush_items;
    this.edit_id = edit_id;
    this.updateMatCount = 0;
    this.optionsControllers = {};
    this.files = {
      left: undefined,
      right: undefined
    };
    this.state = {
      iem_code: iem_code,
      choices: {},
      personalization: {},
      custom_art: {
        left: false,
        right: false
      }
    };

    /** Used for debugging to take state snapshots */
    window.get_designer_state = () => {
      return JSON.parse(JSON.stringify(this.state));
    }

    const storedDesignRaw = localStorage.getItem("jhaudio_designer");

    if (edit_state) {
      this.state = edit_state;
      localStorage.removeItem("jhaudio_designer");
    }
    else if (storedDesignRaw) {
      try {
        const storedDesign = JSON.parse(storedDesignRaw);
        if (storedDesign.iem_code == iem_code) {
          this.state = storedDesign;
        }
        localStorage.removeItem("jhaudio_designer");
      } catch (err) {
        console.error(err);
        console.log(storedDesignRaw);
      }
    }

    this.init();
  }

  get selected_iem() {
    return this.iemsByCode[this.state.iem_code];
  }

  async init() {
    this.startWait();
    const me = this;
    $("body").append(this.$wait);
    this.$wait.fadeIn('fast');

    const $iemSelectContainer = this.$iemSelectContainer = this.$formContainer.find('.iem-selector');
    this.designer = this.$container.find('jha-designer').get(0);

    const controllers = [];

    // cache iem_owners
    for (let owner of this.iem_owners) {
      Reflect.set(this.ownerByName, owner.name, owner);
    }

    // cache iems by names
    for (let iem of this.iems) {
      Reflect.set(this.iemsByCode, iem.item_code, iem);
    }

    // cache swatches for quick lookup
    for (let group of Object.values(this.config)) {
      for (let swatchGroup of group) {

        Reflect.set(this.groupsByName, swatchGroup.name, swatchGroup);

        for (let swatch of swatchGroup.swatches) {
          Reflect.set(this.swatches, swatch.name, swatch);
          if (swatch.colors) {
            for (let color_swatch of swatch.colors) {
              Reflect.set(this.swatches, color_swatch.name, color_swatch);
            }
          }
        }
      }
    }

    if ( !this.settings.disable_faceplace_no_selection ) {
      Reflect.set(this.swatches, "-clear-option-", {
        name: "-clear-option-",
        swatch_label: "No Selection",
        swatch_icon: "",
        material: {
          base_color: '#fff'
        },
        group: {
          group_type: "Art",
          group_label: "Art"
        }
      });
    }

    const adjustOrientation = () => {
      let orientation = getScreenOrientation();
      if (orientation == 0) {
        this.designer.setFocus("center")
      } else {
        this.designer.setFocus("left");
      }
    }

    $(window).on('resize', adjustOrientation);
    adjustOrientation();

    this.imageEditorComponent = new ImageEditorComponent(this);
    this.uploadPricingComponent = new UploadPricingBehaviour(this);

    const $iemSelect = this.$formContainer.find('.iem-selector > button.iem:first');
    this.$formContainer.find('.iem-selector .dropdown-menu .dropdown-item').click(function () {
      let iem_code = $(this).attr('data-item-code');

      // FIX: Simplest solution right now to reset iem selection state
      //      Force a reload to handle different blacklists and option changes.
      me.startWait();
      updateUrlSearch('item_code', iem_code);

      // let iem = Reflect.get(me.iemsByCode, iem_code);

      // if ( iem ) {
      //   let price = iem.price.price_list_rate;
      //   $iemSelect.find('.item').css({ "backgroundImage": `url(${iem.brand_image}` });
      //   $iemSelect.find('.price').text(price);

      //   me.state.iem_code = iem_code;
      //   me.cable = null;
      //   me.$formContainer.find('.all-cables-options .title .label').text("")
      //   if ( me.cableOptionsController ) {
      //     me.cableOptionsController.iemChange();
      //   }
      //   me.updateBlacklist();
      //   me.updatePrice();

      //   me.trigger("iem-change", me.state.iem_code)
      // }
    });

    // wait for component init
    const waitOptionsInitPromises = [];

    const setupCategory = async function ($category, side) {
      let optionsController = null;
      if ($category.hasClass('all-cables-options')) {
        me.cableOptionsController = optionsController = new CableOptions($category, me);
        // NOTE: Important to add controllers into array right after instantiation to keep
        //       category order init process.
        controllers.push(optionsController);
        await optionsController.init();
        $category.on('cable-selected', function (e, data) {
          me.state.cable_code = data.value;
          me.handleCableSelect(e);
        });
      } else if ($category.hasClass('all-boxes-options')) {
        me.boxOptionsController = optionsController = new BoxOptions($category, me);
        // NOTE: Important to add controllers into array right after instantiation to keep
        //       category order init process.
        controllers.push(optionsController);
        await optionsController.init();
        $category.on('box-selected', function (e, data) {
          me.state.box_code = data.value;
          me.handleBoxSelect(e);
        });
      } else if ($category.hasClass('all-lectrosonics-options')) {
        me.lectrosonicsOptionsController = optionsController = new LectrosonicsOptions($category, me);
        // NOTE: Important to add controllers into array right after instantiation to keep
        //       category order init process.
        controllers.push(optionsController);
        await optionsController.init();
        $category.on('lectrosonics-selected', function (e, data) {
          me.state.lectrosonics_code = data.value;
          me.handlelectrosonicsSelect(e);
        });

      } else {
        optionsController = new SwatchGallery($category, me);
        // NOTE: Important to add controllers into array right after instantiation to keep
        //       category order init process.
        controllers.push(optionsController);
        await optionsController.init();
        if (!me.optionsControllers[side]) {
          me.optionsControllers[side] = new Set();
        }
        me.optionsControllers[side].add(optionsController);
        $category.on('swatch-selected', me.handleSwatchSelect.bind(me));
      }

      // Setup int validation for input fields with data-validation="digit"
      $('input[data-validation="digit"]')
        .on("paste", function (e) {
          if (e.originalEvent.clipboardData.getData('text').match(/[^\d]/))
            e.preventDefault(); //prevent the default behaviour
        })
        .keypress(function (event) {
          return (event.charCode !== 8 && event.charCode === 0 || (event.charCode >= 48 && event.charCode <= 57));
        });

      Reflect.set(me.controllers, optionsController.id, optionsController);

      $category.find('.title').click(function () {
        if ($category.hasClass('enabled')) {
          me.$formContainer.find('.category-option.enabled').not($category).addClass('collapsed');
          $category.removeClass('collapsed');
          me.trigger('category-open', $category);
          setTimeout(() => {
            const orientation = getScreenOrientation();

            if (orientation == 90) {
              const top = $category.find('.title').position().top - me.$iemSelectContainer.height();
              me.$formContainer.stop().animate({
                scrollTop: top + me.$formContainer.scrollTop() - me.$formContainer.offset().top
              }, "slow");
            } else {
              $category.get(0).scrollIntoView({ behaviour: 'smooth' });
            }
          }, 60);
        }
      });
    }

    // bind all gallery header/titles clicks to select each gallery
    this.$formContainer.find('.category-option').each(function () {
      const $category = $(this);
      const side = $(this).attr("data-side");
      waitOptionsInitPromises.push(setupCategory($category, side));
    });

    await Promise.all(waitOptionsInitPromises);

    // Setup the animation loop for light lerp animation.
    function animate(time) {
      requestAnimationFrame(animate);
      TWEEN.update(time);
    }
    requestAnimationFrame(animate);

    const lights = { intensity: 1 };
    const lightTween = new TWEEN.Tween(lights);
    this.$lightToggle.change(function () {
      const nightMode = !$(this).is(":checked");
      lightTween
        .to({ intensity: nightMode ? 1 : 0.2 }, 500)
        .onUpdate(function () {
          me.designer.setLightIntensity(lights.intensity);
        })
        .start()
      $(this).parent().find("label").text(nightMode ? "Lights On" : "Lights Off");
    })

    this.personalizeController = new jhaudio.designer.PersonalizeForm(this.$container.find('#personalize'), this);

    this.updatePrice();
    this.updateBlacklist();
    if (me.cableOptionsController) {
      me.cableOptionsController.iemChange();
    }

    $('.jha-designer-footer button.btn-purchase').click(async function () {
      me.purchase($('.jha-designer-footer button.btn-purchase').hasClass('disabled'));
    });

    // load controller states and build material list
    const matLayers = [];

    for (let controller of controllers) {
      if (controller.loadState != undefined) {
        await controller.loadState(matLayers, this.state);
      }
    }

    window.location.hash = '';

    // either start fresh
    if (matLayers.length == 0) {
      // FIX: Odd issue with component not creating proxy method on time
      await new Promise((resolve) => {
        const queueCheck = () => {
          setTimeout(() => {
            if (typeof this.designer.start != "function") {
              queueCheck();
            } else {
              resolve();
            }
          }, 100);
        }
        queueCheck();
      });
      await this.designer.start(undefined, undefined, this.state.iem_code);
    } else {
      // or load all material choices at once
      await asyncSleep(100);
      await this.designer.updateAllLayers(matLayers, "iem", true);
    }
    this.$wait.fadeOut('fast');

    // reveal designer options
    this.$formContainer.fadeIn('slow');
    this.validate();
    this.endWait();
  }

  on(event, callback) {
    this.$container.on(event, callback);
  }

  trigger(event, args) {
    this.$container.trigger(event, args);
  }

  /**
   * Displays spinner and counts the number of times this method is called as to not show/hide
   * the spinner on long running tasks.
   */
  startWait() {
    if (this.updateMatCount == 0) {
      this.$wait.css("display", "flex").hide().fadeIn('fast');
    }
    this.updateMatCount++;
  }

  /**
   * Hides spinner if number of starts matches number of endWait calls
   */
  endWait() {
    this.updateMatCount--;
    if (this.updateMatCount <= 0) {
      this.$wait.fadeOut('fast');
      this.updateMatCount = 0;
    }
  }

  async updateMaterial(mat, side, layer) {
    this.startWait();
    await this.designer.updateMaterial(mat, side, layer);
    this.endWait();
  }

  updateBlacklist() {
    let iem = Reflect.get(this.iemsByCode, this.state.iem_code);
    this.leftBlacklist = new Set();
    this.rightBlacklist = new Set();

    // reset blacklisted swatches
    this.$container.find('.blacklisted').removeClass("blacklisted");

    if (iem.designer_options) {
      // Blacklist based on IEM selected
      for (let material of iem.designer_options.material_blacklist) {
        this.leftBlacklist.add(material);
        this.rightBlacklist.add(material);
      }

      // Blacklist based on material blacklist
      for (let choice of Object.values(this.state.choices)) {
        let side = choice.side;

        if (choice.name in this.swatches) {
          const swatch = this.swatches[choice.name];

          if (swatch.material_blacklist) {
            for (let material of swatch.material_blacklist) {
              if (side === "left") {
                this.leftBlacklist.add(material);
              } else if (side === "right") {
                this.rightBlacklist.add(material);
              }

            }
          }
          if (choice.options) {
            if (choice.options.disable_faceplates) {
              const $swatchGroup = this.$container.find(`.category-option[data-layer="Face-Plate"][data-side="${choice.side.toLowerCase()}"]`);
              $swatchGroup.addClass('blacklisted');
            }

            if (choice.options.disable_art) {
              const $swatchGroup = this.$container.find(`.category-option[data-layer="Art"][data-side="${choice.side.toLowerCase()}"]`);
              $swatchGroup.addClass('blacklisted');
            }

          }
        }
      }

      let groupsWithBlacklistedSwatches = new Set();

      const markBlacklist = (material, side) => {
        const $swatch = this.$container.find(`[data-material="${material}"][data-side="${side}"]`);

        if ($swatch.length > 0) {
          $swatch.addClass("blacklisted");
          const parentSwatch = $swatch.attr('data-parent-swatch');
          if (parentSwatch) {
            groupsWithBlacklistedSwatches.add(parentSwatch);
          }
        }
      }


      // mark swatches
      for (let material of Array.from(this.leftBlacklist)) {
        markBlacklist(material, "left");
      }

      for (let material of Array.from(this.rightBlacklist)) {
        markBlacklist(material, "right");
      }

      // Find all affected swatches and check if all of its child colors have been blacklisted
      // if so, hide swatch as well.
      for (let swatchName of Array.from(groupsWithBlacklistedSwatches)) {
        const $swatch = this.$container.find(`.swatch[data-name=${swatchName}]`);

        if ($swatch.length > 0) {
          const visibleChildMaterials = this.$container.find(`[data-parent-swatch=${swatchName}]`).not('.blacklisted').length;
          if (visibleChildMaterials == 0) {
            $swatch.addClass('blacklisted')
          }
        }
      }

      // check all swatch groups
      this.$container.find('.swatch-group').each(function () {
        const $group = $(this);
        let groupName = $group.attr('data-name');
        let $groupBtn = $group.closest('.category-option').find(`.swatch-group-btn[data-name=${groupName}]`);
        if ($groupBtn.attr('data-name') != 'upload') {
          if ($group.find('.swatch').length == $group.find('.swatch.blacklisted').length) {
            $groupBtn.hide();
          } else {
            $groupBtn.show();
          }
        }
      });
    }

  }

  getIemOwner(name) {
    return Reflect.get(this.ownerByName, name);
  }

  async handlelectrosonicsSelect(e) {
    const $current = $(e.target);
    let iem = Reflect.get(this.iemsByCode, this.state.iem_code);

    this.lectrosonics = iem.designer_options.lectrosonics.find((lectrosonics) => lectrosonics.item_code == this.state.lectrosonics_code);

    console.log(this.state.lectrosonics_code, this.lectrosonics);

    if ( this.lectrosonics ) {
      $current.find('.label').text(`: ${this.lectrosonics.item_label}`);
      const price = this.lectrosonics.override_price ? this.lectrosonics.item_price : this.lectrosonics.price.price_list_rate;
      if (price > 0) {
        $current.find('.price').text(`+ $${price.toFixed(2)}`);
      } else {
        $current.find('.price').text('');
      }
    } else {
      $current.find('.label').text(`: None`);
      $current.find('.price').text('');
    }

    this._moveNextCategory($current, "Handle Lectrosonics Select");
  }

  async handleCableSelect(e) {
    const $current = $(e.target);
    let iem = Reflect.get(this.iemsByCode, this.state.iem_code);

    this.cable = iem.designer_options.cables.find((cable) => cable.item_code == this.state.cable_code);

    $current.find('.label').text(`: ${this.cable.item_label}`);
    const cable_price = this.cable.override_price ? this.cable.item_price : this.cable.price.price_list_rate;
    if (cable_price > 0) {
      $current.find('.price').text(`+ $${cable_price.toFixed(2)}`);
    } else {
      $current.find('.price').text('');
    }

    this._moveNextCategory($current, "Handle Cable Select");

  }

  async handleBoxSelect(e) {
    const $current = $(e.target);
    let iem = Reflect.get(this.iemsByCode, this.state.iem_code);

    this.box = iem.designer_options.boxes.find((box) => box.item_code == this.state.box_code);

    $current.find('.label').text(`: ${this.box.item_label}`);
    const box_price = this.box.override_price ? this.box.item_price : this.box.price.price_list_rate;
    if (box_price > 0) {
      $current.find('.price').text(`+ $${box_price.toFixed(2)}`);
    } else {
      $current.find('.price').text('');
    }

    this._moveNextCategory($current, "Handle Box Select");
  }

  async handleSwatchSelect(e, data) {
    const $current = $(e.target);
    const swatch = Reflect.get(this.swatches, data.name);

    // Defines item object which stores price information
    let item = undefined;
    if (swatch.item && swatch.item.price) {
      item = {
        item_code: swatch.item.item_code,
        price: {
          price_list_rate: swatch.item.price.price_list_rate
        }
      };
    }

    Reflect.set(this.state.choices, data.id, {
      name: swatch.name,
      categoryId: data.id,
      item: item,
      options: swatch.options,
      group: {
        name: data.group.name,
        group_type: data.group.group_type
      },
      swatch_label: swatch.swatch_label,
      side: data.side,
      parent_swatch_name: data.parentSwatchName
    });

    // Reset custom art when a swatch is selected
    if (["Art-left", "Art-right"].includes(data.id)) {
      this.state.custom_art[data.side] = false;
    }

  }

  _enableNextCategory($current, collapsed, $forceNext) {
    if (!$current.hasClass('has-selection')) {
      let $next = $forceNext || $current.next();
      if (!$forceNext) {
        while ($next.length > 0 && ($next.hasClass('not-available') || !$next.is(':visible') || $next.hasClass('blacklisted'))) {
          $next = $next.next();
        }
      }

      if ($next.length > 0) {
        $next.addClass('enabled');

        if (collapsed) {
          $next.addClass('collapsed');
        }
      }

      return $next;
    }
  }

  _moveNextCategory($current, from, force, $next) {
    if (!$current.hasClass('has-selection') || force) {
      let $next = this._enableNextCategory($current, false, $next);

      $current.addClass('collapsed');
      if ($next && $next.length > 0) {
        $next.removeClass('collapsed');
        this.trigger('category-open', $next);
        setTimeout(() => {
          const orientation = getScreenOrientation();

          if (orientation == 90) {
            const top = $next.find('.title').position().top - this.$iemSelectContainer.height();
            this.$formContainer.stop().animate({
              scrollTop: top + this.$formContainer.scrollTop() - this.$formContainer.offset().top
            }, "slow");
          } else {
            $next.get(0).scrollIntoView({ behaviour: 'smooth' });
          }
        }, 60);
      }
    }

    this.updatePrice();
  }

  validate() {

    // Ensure the next visible category without a value is enabled
    const $categories = this.$formContainer.find('.category-option:not(.not-available):not(.blacklisted):visible');

    // Don't advance to next category if editor is active
    if (!this.imageEditorComponent || !this.imageEditorComponent.active) {
      let previous_has_selection = false;
      $categories.each(function () {
        const $cat = $(this);
        if ($cat.hasClass('has-selection')) {
          previous_has_selection = $cat;
        } else if (previous_has_selection) {
          $cat.addClass('enabled');
          $categories.filter('.has-selection').not($cat).addClass('collapsed');
          $cat.removeClass('collapsed');
          return false; // break from loop
        }
      });
    }

    // Count categories with selections
    let inactive = $categories.length;
    inactive -= $categories.filter('.has-selection').length;
    let valid = inactive == 1;

    // last category is the personalize form. It will never have .has-selection set
    if (this.personalizeController) {
      valid = valid && this.personalizeController.validate();

      // remove -1 id as it is used to flag a new iem owner on UI
      const iem_owner = this.personalizeController.iem_owner == "-1" ? "" : this.personalizeController.iem_owner;

      this.state.personalization = {
        iem_owner,
        name_on_case: this.personalizeController.name_on_case,
        first_name: this.personalizeController.first_name,
        last_name: this.personalizeController.last_name,
        band_name: this.personalizeController.band_name,
        email_id: this.personalizeController.email_id,
        qty: this.personalizeController.qty || 1,
        instructions: this.personalizeController.instructions,
        ship_date: this.personalizeController.ship_date,
        measurements: this.personalizeController.measurements,
        rush_item: this.personalizeController.rush_item
      }
    }

    if (valid) {
      $('.jha-designer-footer button.btn-purchase').removeClass('disabled');
      $('#purchase-button').tooltip('dispose');
    } else {
      $('#purchase-button').tooltip('dispose');
      let tooltipText = $('.category-option.enabled').last();
      let element = $(tooltipText).find(".title > .text")[0];
      if (element && element.length > 0 && `${element.innerText.toLowerCase()}` == 'personalize') {
        $('#purchase-button').tooltip({ "title": `Please fill the personalize form` });
      }
      else if (element != undefined) {
        $('#purchase-button').tooltip({
          "title": `Please select ${element.innerText.toLowerCase()}`
        });
      }
      $('.jha-designer-footer button.btn-purchase').addClass('disabled');
    }

    this.trigger('validate-form')
  }

  addWarnings(id, warning) {
    if (!this.state.warnings) {
      this.state.warnings = {}
    }

    this.state.warnings[id] = warning;
  }

  updatePrice() {
    this.validate();
    let iem = Reflect.get(this.iemsByCode, this.state.iem_code);
    let price = iem.price.price_list_rate;

    // accumulate individual choice prices
    for (let choice of Object.values(this.state.choices)) {
      if (choice.item) {
        price += choice.item.price.price_list_rate;
      }
    }

    // accumulate cable selection price
    if (this.cable && this.cable.override_price) {
      price += this.cable.item_price;
    } else if (this.cable && !this.cable.override_price) {
      price += this.cable.price.price_list_rate;
    }

    if ( this.lectrosonics && this.lectrosonics.override_price) {
      price += this.lectrosonics.item_price;
    } else if ( this.lectrosonics && !this.lectrosonics.override_price) {
      price += this.lectrosonics.price.price_list_rate;
    }

    // accumulate box selection price
    if (this.box && this.box.override_price) {
      price += this.box.item_price;
    } else if (this.box && !this.box.override_price) {
      price += this.box.price.price_list_rate;
    }

    // accumulate rush item price
    if (this.personalizeController && this.personalizeController.rush_item) {
      price += this.rush_items[this.personalizeController.rush_item].price.price_list_rate;
    }

    // price uploads
    let sides = ["left", "right"];
    for (const side of sides) {
      const art = this.state.custom_art[side];
      if (art && art.charge) {
        price += this.settings.art_upload_fee_item.price.price_list_rate;
      }
    }

    this.$footer.find('.price').text(`$${price.toFixed(2)}`);
  }

  /**
   * Returns true if current iem includes a case or requires user to pick a case.
   * @returns {boolean}
   */
  iemRequiresCase() {
    const iem = Reflect.get(this.iemsByCode, this.state.iem_code);
    if (iem.designer_options.boxes.length > 0) {
      return true;
    } else {
      // use old api in case of other core requiring it.
      return this.iemCase() != null;
    }
  }

  iemCase() {
    const iem = Reflect.get(this.iemsByCode, this.state.iem_code);
    if (iem) {
      return iem.designer_options.box
    }

    return false;
  }

  requireLogin() {
    localStorage.setItem("jhaudio_designer", JSON.stringify(this.state));
    $('#login-popup').fadeIn('fast');
  }

  async purchase(disabled) {

    if (disabled) {
      this.personalizeController.fieldFeedback();
      return;
    }

    if (! await this.personalizeController.onSubmitForm()) {
      return;
    }

    const group = Date.now();
    const iem = Reflect.get(this.iemsByCode, this.state.iem_code);
    const qty = this.state.personalization.qty || 1;

    if (!frappe.is_user_logged_in()) {
      this.requireLogin();
      return;
    }

    $('.jha-designer-footer button.btn-purchase').prop('disabled', true);
    try {
      this.$wait.fadeIn('fast');

      // create left and right arrays to separate usage in specs template
      let left = [];
      let right = [];
      let removeChoices = [];
      for (let choice of Object.values(this.state.choices)) {
        if (choice.side.toLowerCase() == 'left') {
          if (this.state.custom_art.left && choice.group.group_type == "Art") {
            removeChoices.push(choice);
          } else {
            left.push(choice);
          }
        } else {
          if (this.state.custom_art.right && choice.group.group_type == "Art") {
            removeChoices.push(choice);
          } else {
            right.push(choice);
          }
        }
      }

      for (let x of removeChoices) {
        delete this.state.choices[x.categoryId];
      }

      if (this.state.custom_art.left) {
        left.unshift({
          group: { group_type: "Art" },
          swatch_label: "Custom"
        })
      }
      if (this.state.custom_art.right) {
        right.unshift({
          group: { group_type: "Art" },
          swatch_label: "Custom"
        })
      }

      let uploadTasks = [];
      let leftUpload = {};
      let rightUpload = {};
      let totalUniqueUploads = 0;
      let fileNames = {};

      if (this.state.custom_art["left"] && this.files.left) {
        fileNames[this.files.left.name] = leftUpload;
        leftUpload.file_obj = this.files.left;
        leftUpload.side = "left";
        leftUpload.name = this.files.left.name;
        if (!this.files.left.synthetic) {
          uploadTasks.push(await uploadFile(leftUpload, false, 'Home/Custom Art'));
        } else {
          fileNames[this.files.left.name].doc = {
            file_url: this.files.left.url,
            file_name: this.files.left.name
          }
        }
      }

      if (this.state.custom_art["right"] && this.files.right && fileNames[this.files.right.name] == undefined) {
        let side = "right";
        fileNames[this.files.right.name] = rightUpload;
        rightUpload.file_obj = this.files.right;
        rightUpload.side = side;
        rightUpload.name = this.files.right.name;
        if (!this.files.right.synthetic) {
          uploadTasks.push(await uploadFile(rightUpload, false, 'Home/Custom Art'));
        } else {
          fileNames[this.files.right.name].doc = {
            file_url: this.files.right.url,
            file_name: this.files.right.name
          }
        }
      }

      const imageUpload = {
        file_obj: await (await fetch(await this.designer.queueScreenshot(512, 512))).blob(),
        name: `${this.state.iem_code}-custom.png`,
      };
      uploadTasks.push(await uploadFile(imageUpload, false, 'Home/Custom Art'));

      totalUniqueUploads = Object.keys(fileNames).length;

      // wait for uploads
      if (uploadTasks.length > 0) {
        await Promise.all(uploadTasks);
      }

      const custom_left_art = leftUpload.doc != undefined ? leftUpload.doc.file_url : undefined;
      const custom_right_art = rightUpload.doc != undefined ? rightUpload.doc.file_url : undefined;
      const custom_left_art_name = leftUpload.doc != undefined ? leftUpload.doc.file_name : undefined;
      const custom_right_art_name = rightUpload.doc != undefined ? rightUpload.doc.file_name : undefined;
      if (this.state.custom_art["left"] && custom_left_art) {
        this.state.custom_art["left"].url = custom_left_art;
        this.state.custom_art["left"].file_name = custom_left_art_name;
      }

      if (this.state.custom_art["right"] && custom_right_art) {
        this.state.custom_art["right"].url = custom_right_art;
        this.state.custom_art["right"].file_name = custom_right_art_name;
      }

      // dumb way of keeping yes/no consistent with old value usage in case we load
      // an old designer data export.
      const yesno = (value) => {
        if (value === true || value === 1 || value === "1" || (value && value.toLowerCase() === "yes")) {
          return "yes"
        }
        return "no"
      }

      const is_art_missing = (side) => {
        if (side in this.state.custom_art) {
          return this.state.custom_art[side].requires_artwork;
        }
        return false;
      }

      const extra_fields = {
        iem_owner: this.state.personalization.iem_owner,
        designer_band_name: this.state.personalization.band_name,
        designer_owner_first_name: this.state.personalization.first_name,
        designer_owner_last_name: this.state.personalization.last_name,
        designer_owner_email: this.state.personalization.email_id,
        designer_owner_measurements: yesno(this.state.personalization.measurements),
        custom_left_art,
        custom_right_art,
        instructions: this.state.personalization.instructions || '',
        rush_item: this.state.personalization.rush_item ? 1 : 0,
        ship_by_date: '',
        artwork_missing: (is_art_missing("left") || is_art_missing("right")) ? 1 : 0
      }

      const design_snapshot_url = imageUpload.doc != undefined ? imageUpload.doc.file_url : undefined;
      this.state.design_snapshot_url = design_snapshot_url;

      // seed our add_multiple with base iem product
      const items = [{
        item_code: this.state.iem_code,
        qty,
        group,
        extra_fields: Object.assign({
          designer_data: JSON.stringify(this.state)
        }, extra_fields),
        image: design_snapshot_url,
        warnings: this.state.warnings
      }];

      // add any necessary items used to charge fees on options.
      for (let choice of Object.values(this.state.choices)) {
        let side = choice.side.charAt(0).toUpperCase() + choice.side.slice(1);
        if (choice.item) {
          items.push({
            item_code: choice.item.item_code,
            qty,
            group,
            sub_group: side,
            description: `${side} ${choice.group.group_type}`
          });
        }
      }

      // insert cable selection
      if (this.cable) {
        items.push({
          item_code: this.cable.item_code,
          qty,
          group,
          sub_group: "Cable",
          designer_include: {
            type: "cable",
            item_code: this.state.iem_code
          }
        })
      }

      if (this.box) {
        items.push({
          item_code: this.box.item_code,
          qty,
          group,
          extra_fields: Object.assign({
            designer_name_on_case: this.state.personalization.name_on_case,
          }, extra_fields),
          sub_group: "Case",
          designer_include: {
            type: "box",
            user_picked: true,
            item_code: this.state.iem_code
          }
        })
      } else {
        // insert box
        if (iem.designer_options.box) {
          items.push({
            item_code: iem.designer_options.box,
            qty,
            group,
            extra_fields: Object.assign({
              designer_name_on_case: this.state.personalization.name_on_case,
            }, extra_fields),
            sub_group: "Case",
            designer_include: {
              type: "box",
              user_picked: false,
              item_code: this.state.iem_code
            }
          })
        }
      }

      // Insert lectrosonics system
      if (this.lectrosonics) {
        items.push({
          item_code: this.lectrosonics.item_code,
          qty,
          group,
          sub_group: "Lectrosonics",
          designer_include: {
            type: "lectrosonics",
            item_code: this.state.iem_code
          }
          
        })
      }

      let sides = ['left', 'right'];
      let art_url = null;
      for (let s of sides) {
        let art = this.state.custom_art[s];

        // cache image url, when both sides use the same image only the left side
        // will contain the url
        if (art.url) {
          art_url = art.url;
        }

        if (art) {
          items.push({
            item_code: this.settings.art_upload_fee_item.item_code,
            qty,
            group,
            sub_group: s,
            image: art_url,
            description: `${s} side custom image upload`,
            designer_include: {
              type: "upload",
              side: s,
              charge: art.charge,
              item_code: this.state.iem_code
            }
          });
        }
      }

      if (this.personalizeController.rush_item) {
        items.push({
          item_code: this.personalizeController.rush_item,
          qty: 1,
          group,
          sub_group: "Rush Fee"
        })
      }

      const r = await frappe.call("jhaudio.cart.add_multiple", { items, edit_id: this.edit_id });
      window.showItemAddedToCartModal(true);

    } catch (err) {
      console.log(err);
    } finally {
      this.personalizeController.onSubmitFormCompleted();
      $('.jha-designer-footer button.btn-purchase').prop('disabled', false);
      this.$wait.fadeOut('fast');
    }
  }
}